// packages import
import PrintIcon from '@mui/icons-material/Print'
import { Box, Button, CircularProgress, DialogActions, Divider, Stack, TextField, Typography } from '@mui/material'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import moment from 'moment'
import { FC, useContext, useEffect, useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'
// component import
import { Alert } from '../common/Alert'
import { CustomModal } from '../common/CustomModal'
import { EmptyData } from '../common/EmptyData'
import { DashboardAssignedTaskSkeleton } from '../skeletons/DashboardAssignedTaskSkeleton'
import { PrintTasks } from './PrintTasks'
/// others
import { ExternalUserRoleRole, UserTasksPayload, useFetchAllAssignedTasksLazyQuery } from '../../../generated'
import {
  BACKEND_DATE_FORMAT,
  CANCEL,
  CONFLICT_EXCEPTION,
  GRAPHQL_QUERY_POLICY,
  NOT_APPLICABLE,
  SOMETHING_WENT_WRONG,
  STANDARD_DATE_FORMAT
} from '../../constants'
import { AppContext } from '../../context'
import { ActionType } from '../../context/AppContextReducer'
import { PrintTasksModalProps } from '../../interfaceTypes'
import { flexCenter } from '../../theme/styleConstants'
import { titleize } from '../../utils'

export const PrintTasksModal: FC<PrintTasksModalProps> = ({ selectedUser }) => {
  const [filterStartDate, setFilterStartDate] = useState<string | null>(
    moment(new Date()).startOf('week').format(BACKEND_DATE_FORMAT).toString()
  )
  const [filterEndDate, setFilterEndDate] = useState<string | null>(
    moment(new Date()).endOf('week').format(BACKEND_DATE_FORMAT).toString()
  )
  const { isDeleteOpen, dispatch } = useContext(AppContext)
  const [userCaseTask, setUserCaseTask] = useState<UserTasksPayload['userCaseTasks']>()
  const { id } = selectedUser || {}
  const componentRef = useRef(null)

  const sortedEvents = userCaseTask?.slice().sort(function (firstEvent, secondEvent) {
    const firstEventHearingAt = moment(firstEvent!.followUpDate!)
    const secondEventHearingAt = moment(secondEvent!.followUpDate!)

    return firstEventHearingAt!.date() - secondEventHearingAt!.date()
  })

  const [fetchAllAssignedTasks, { loading }] = useFetchAllAssignedTasksLazyQuery({
    ...GRAPHQL_QUERY_POLICY,
    variables: undefined,

    onError(error) {
      if (error.message === CONFLICT_EXCEPTION) Alert.error(CONFLICT_EXCEPTION)
      else Alert.error(SOMETHING_WENT_WRONG)
    },

    onCompleted(data) {
      if (data) {
        const { fetchAllAssignedTasks } = data

        if (fetchAllAssignedTasks) {
          const { response, userCaseTasks } = fetchAllAssignedTasks
          const { status } = response || {}

          if (status === 200 && userCaseTasks) {
            setUserCaseTask(userCaseTasks)
          }
        }
      }
    }
  })

  useEffect(() => {
    if (id) {
      fetchAllAssignedTasks({
        variables: {
          tasksByAssigneeInput: {
            assigneeId: id,
            from: moment(filterStartDate?.toString()).format(BACKEND_DATE_FORMAT),
            to: moment(filterEndDate?.toString()).format(BACKEND_DATE_FORMAT)
          }
        }
      })
    }
    // eslint-disable-next-line
  }, [filterStartDate, filterEndDate, id])

  const handleClose = () => dispatch({ type: ActionType.SET_IS_DELETE_OPEN, isDeleteOpen: false })

  const handlePrint = useReactToPrint({
    content: () => componentRef.current
  })

  const filterStartDateHandle = (date: string | null) => {
    setFilterStartDate(moment(date?.toString()).format(STANDARD_DATE_FORMAT))
  }

  const filterEndDateHandle = (date: string | null) => {
    setFilterEndDate(moment(date?.toString()).format(STANDARD_DATE_FORMAT))
  }

  const isEmpty = userCaseTask?.length === 0 && !loading

  return (
    <CustomModal title="Print User Tasks" isOpen={isDeleteOpen} handleClose={handleClose}>
      <Box display="flex" gap="10px" maxWidth={390}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            label="From"
            inputFormat={STANDARD_DATE_FORMAT}
            value={filterStartDate}
            onChange={value => filterStartDateHandle(value)}
            renderInput={params => <TextField size="small" {...params} />}
            minDate={moment(new Date()).subtract(24, 'months').format()}
            maxDate={moment(filterEndDate?.toString()).format(STANDARD_DATE_FORMAT)}
          />
        </LocalizationProvider>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DesktopDatePicker
            label="To"
            inputFormat={STANDARD_DATE_FORMAT}
            value={filterEndDate}
            onChange={value => filterEndDateHandle(value)}
            renderInput={params => <TextField size="small" {...params} />}
            minDate={moment(filterStartDate).format()}
          />
        </LocalizationProvider>
      </Box>

      {loading ? (
        <Box mt="14px">
          <DashboardAssignedTaskSkeleton noOfRows={4} />
        </Box>
      ) : isEmpty ? (
        <Box height="350px" sx={flexCenter}>
          <EmptyData title="No tasks available" height="624px" iconSize="100px" fontSize="20px" />
        </Box>
      ) : (
        <Box height="278px" overflow="auto" mt={2}>
          {sortedEvents &&
            sortedEvents.map(taskItem => {
              const { task, litigation, followUpDate } = taskItem || {}
              const { id, title } = task || {}
              const { title: caseTitle, caseNumber, caseToExternalUserToExternalUserRole } = litigation || {}
              const client = caseToExternalUserToExternalUserRole?.find(
                item =>
                  item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles &&
                  item?.externalUserToExternalUserRole?.externalUser?.externalUserRoles[0]?.externalUserRole?.role ===
                    ExternalUserRoleRole.Client
              )?.externalUserToExternalUserRole.externalUser
              const { firstName: clientFirstName, lastName: clientLastName } = client || {}
              const clientFullName =
                clientFirstName || clientLastName ? `${clientFirstName} ${clientLastName}` : NOT_APPLICABLE

              return (
                <>
                  <Stack direction="row" key={id} justifyContent="space-between" alignItems="center" my={1}>
                    <Box>
                      <Typography>{titleize(title || '')}</Typography>
                      <Typography fontWeight={500} fontSize="14px" paddingRight="5px">
                        {`(#${caseNumber} ${caseTitle}) `}
                        {clientFullName}
                      </Typography>
                    </Box>

                    <Typography fontWeight={500} mr={1}>
                      {moment(followUpDate).format(STANDARD_DATE_FORMAT)}
                    </Typography>
                  </Stack>
                  <Divider />
                </>
              )
            })}
        </Box>
      )}

      <Box style={{ display: 'none' }}>
        <PrintTasks sortedEvents={sortedEvents} ref={componentRef} selectedUser={selectedUser} />
      </Box>

      <DialogActions sx={{ padding: '30px 0 0', flexWrap: 'wrap' }}>
        <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
          {CANCEL}
        </Button>
        <Button
          startIcon={<PrintIcon style={{ color: 'white' }} />}
          color="primary"
          variant="contained"
          disabled={loading || isEmpty}
          onClick={handlePrint}
          endIcon={loading && <CircularProgress size={20} color="inherit" />}
          aria-label="print notes"
        >
          Print
        </Button>
      </DialogActions>
    </CustomModal>
  )
}
